import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuthentication } from '@givebacks/api';
import {
  IListUserCauseSelectionsResponse,
  useAddCauseSelection,
  useDeleteUserCauseSelection,
  useListUserCauseSelections,
} from 'core/Api';
import { getErrorMessage_v2 } from 'core/Utils';
import { useSearchParams } from 'react-router-dom';
import { Box, ButtonMantine, ButtonProps, ElementProps, Text, cx, notifications } from 'ui';
import { useDisclosure } from 'ui/Hooks';
import { IconCheck } from 'ui/Icons';

import { useNavigateToAuth } from '../../../../hooks/useNavigateToAuth/useNavigateToAuth';
import { useCausePageContext } from '../../CauseContext';
import classes from './FollowButton.module.css';
import { UnfollowModal } from './UnfollowModal';

type AnimationState = 'idle' | 'loading' | 'checkmark' | 'complete';

const fauxWaiting = (milliseconds: number) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, milliseconds);
  });
};

const LoadingDots = () => {
  return (
    <Box className={classes.FollowButton__loadingDotsContainer}>
      {[0, 1, 2].map((index) => (
        <Box key={index} className={classes.FollowButton__loadingDot} />
      ))}
    </Box>
  );
};

const AnimatedCheckmark = () => {
  return (
    <Box className={classes.FollowButton__checkmarkContainer}>
      <IconCheck size={28} className={classes.FollowButton__animatedCheckmark} />
    </Box>
  );
};

type FollowButtonProps = ElementProps<'button'> & ButtonProps;

const FollowButton = ({ className, ...props }: FollowButtonProps) => {
  const [isUnfollowModalOpen, unfollowModalHandler] = useDisclosure(false);
  const { cause } = useCausePageContext();
  const { isAuthenticated } = useAuthentication();
  const { data: userCauses, refetch: refetchUserCauseSelections } = useListUserCauseSelections();
  const { handleNavigateToAuth } = useNavigateToAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [animationState, setAnimationState] = useState<AnimationState>('idle');

  const { mutateAsync: addCauseSelection, isPending: isAddingCauseSelection } =
    useAddCauseSelection();
  const { mutate: deleteCauseSelection, isPending: isDeletingCauseSelection } =
    useDeleteUserCauseSelection();

  const addToMyCauses = useCallback(async () => {
    try {
      setAnimationState('loading');
      await addCauseSelection({ causeId: cause.uuid });
      setAnimationState('checkmark');
      await fauxWaiting(1200);
      setAnimationState('complete');
      void refetchUserCauseSelections();
    } catch (err) {
      setAnimationState('idle');
      notifications.show({
        variant: 'error',
        message: getErrorMessage_v2(err),
      });
    }
  }, [addCauseSelection, cause.uuid, refetchUserCauseSelections, setAnimationState]);

  const handleRemoveFromMyCauses = useCallback(() => {
    deleteCauseSelection(
      { causeId: cause.uuid },
      {
        onSuccess() {
          notifications.show({
            variant: 'success',
            message: `Unfollowed ${cause.name}`,
          });
          unfollowModalHandler.close();
          setAnimationState('idle');
          void refetchUserCauseSelections();
        },
        onError() {
          notifications.show({
            variant: 'error',
            message: `Could not unfollow ${cause.name}`,
          });
        },
      }
    );
  }, [
    cause.name,
    cause.uuid,
    deleteCauseSelection,
    refetchUserCauseSelections,
    unfollowModalHandler,
  ]);

  const getIsFavorited = useCallback(
    (userCauses: IListUserCauseSelectionsResponse['cause_selections']) =>
      !!cause.uuid &&
      (userCauses ?? []).map((selection) => selection.cause_id).includes(cause.uuid),
    [cause.uuid]
  );

  const isFavorited = getIsFavorited(userCauses ?? []);

  const handleFollowClick = useCallback(() => {
    if (!isAuthenticated) {
      handleNavigateToAuth({
        page: 'sign-in',
        redirect: `/causes/${cause.uuid}`,
        additionalParams: {
          action: 'follow',
        },
      });
      return;
    }

    const isFavorited = getIsFavorited(userCauses ?? []);

    if (isFavorited || animationState === 'complete') {
      unfollowModalHandler.open();
    } else if (animationState === 'idle') {
      void addToMyCauses();
    }
  }, [
    isAuthenticated,
    getIsFavorited,
    userCauses,
    animationState,
    handleNavigateToAuth,
    cause.uuid,
    unfollowModalHandler,
    addToMyCauses,
  ]);

  const displayAsFavorited =
    animationState === 'complete' ||
    (animationState === 'idle' && getIsFavorited(userCauses ?? []));

  const buttonIcon = useMemo(() => {
    if (animationState === 'loading' || isAddingCauseSelection) {
      return <LoadingDots />;
    } else if (animationState === 'checkmark') {
      return <AnimatedCheckmark />;
    }

    return null;
  }, [animationState, isAddingCauseSelection]);

  const buttonText = useMemo(() => {
    if (displayAsFavorited) {
      return 'Following';
    }

    return 'Follow';
  }, [displayAsFavorited]);

  const isDisabled = animationState === 'loading' || animationState === 'checkmark';

  // This hook is used to handle initiating the follow flow
  // when the user was signed out and clicked the follow button
  // and then signed in, redirecting them to the cause page with the follow action
  useEffect(() => {
    const action = searchParams.get('action');

    if (isAuthenticated && action === 'follow' && !isFavorited) {
      requestAnimationFrame(() => {
        void addToMyCauses();
      });

      // Clean up the URL
      searchParams.delete('action');
      setSearchParams(searchParams);
    }
  }, [
    addToMyCauses,
    getIsFavorited,
    refetchUserCauseSelections,
    isAuthenticated,
    searchParams,
    isFavorited,
    setSearchParams,
  ]);
  return (
    <>
      <ButtonMantine
        variant="subtle"
        className={cx(classes.FollowButton, className)}
        classNames={{
          label: classes.FollowButton__label,
        }}
        onClick={handleFollowClick}
        disabled={isDisabled}
        {...props}
      >
        {buttonIcon}
        <Text
          mod={{
            'animation-state': animationState,
          }}
          className={classes.FollowButton__buttonText}
        >
          {buttonText}
        </Text>
      </ButtonMantine>
      <UnfollowModal
        causeName={cause.name}
        opened={isUnfollowModalOpen}
        onClose={unfollowModalHandler.close}
        onUnfollow={handleRemoveFromMyCauses}
        loading={isDeletingCauseSelection}
      />
    </>
  );
};

export { FollowButton };
